import { styled } from '@nykaa/ui-components';
import { SPACING } from './constants';
import { SPACING_TYPE } from './types';

export const Main = styled.div<{
  inFocus: boolean;
  isDisabled: boolean;
  isValid: boolean;
  isError: boolean;
  isMultiline: boolean;
  spacingType: SPACING_TYPE;
}>`
  background: rgba(101, 119, 134, 0.12);
  position: relative;
  height: ${({ spacingType, isMultiline }) => {
    if (isMultiline) {
      return 'auto';
    }

    return SPACING[spacingType];
  }};
  border-bottom: 1px solid ${({ theme }) => theme.colors.textOutline};

  ${({ isDisabled, inFocus, isValid, isError, theme }) => {
    if (isError) {
      return `
                border-bottom: 2px solid transparent;
                background: rgba(182, 0, 0, 0.12);
            `;
    }
    if (isDisabled) {
      return `
                pointer-events: none; 
                opacity: 0.6; 
                cursor: not-allowed
            `;
    }
    if (isValid) {
      return `
                border-bottom: 2px solid ${theme.colors.textOutline};
            `;
    }

    if (inFocus) {
      return `
                border-bottom: 2px solid transparent;
                background: rgba(101, 119, 134, 0.08);
            `;
    }
  }}
`;

export const Label = styled.label<{
  inFocus: boolean;
  isValid: boolean;
  isCompact: boolean;
  isMultiline: boolean;
}>`
  color: ${({ theme }) => theme.colors.textDisabled};
  ${({ theme }) => theme.typography.bodyLarge};
  letter-spacing: 0.25px;
  position: absolute;
  pointer-events: none;
  transition: 0.2s ease all;
  transform-origin: top left;
  ${({ inFocus, isValid, isCompact, isMultiline, theme }) => {
    if (isCompact) {
      return `
                transform: scale(0.75) translate3d(-16px, -100%, 0)
            `;
    }

    if (inFocus || isValid) {
      if (isMultiline) {
        return `
                    transform: scale(0.75) translate3d(0, -8px, 0);
                    color: ${theme.colors.textSecondary};
                `;
      }
      return `
                transform: scale(0.75) translate3d(0, -100%, 0);
                color: ${theme.colors.textSecondary};
            `;
    }

    if (isMultiline) {
      return `
                transform: scale(1) translate3d(0, 0, 0);
            `;
    }

    return 'transform: scale(1) translate3d(0, -50%, 0)';
  }};

  left: 0;
  top: ${({ isCompact, isMultiline }) => {
    if (isCompact) {
      return '0';
    }
    if (isMultiline) {
      return '16px';
    }
    return '50%';
  }};
`;

export const FlexWrapper = styled.div<{
  hasTrailingComponent?: boolean;
  hasLeadingComponent: boolean;
}>`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  position: relative;
  padding: ${({ theme }) => `0 ${theme.spacing.spacing80}`};
  ${({ hasTrailingComponent }) => hasTrailingComponent && 'padding-right: 0;'};
  ${({ hasLeadingComponent }) => hasLeadingComponent && 'padding-left: 0;'};
`;

export const InputWrapper = styled(FlexWrapper)`
  align-items: flex-end;
  padding: 0;
`;

export const Prefix = styled.div`
  display: inline-block;
  margin: ${({ theme }) => `0 ${theme.spacing.spacing40} ${theme.spacing.spacing20} 0`};
  ${({ theme }) => theme.typography.bodyLarge};
  letter-spacing: 0.25px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export const Suffix = styled(Prefix)`
  margin: ${({ theme }) => `0 0 ${theme.spacing.spacing20} ${theme.spacing.spacing40}`};
`;

export const InputElement = styled.input<{ inFocus: boolean; isMultiline: boolean }>`
  ${({ theme }) => theme.typography.bodyLarge};
  letter-spacing: 0.25px;
  margin: ${({ theme }) => `0 0 ${theme.spacing.spacing20} 0`};

  background: transparent;
  width: 100%;
  display: block;
  border: none;
  color: ${({ theme }) => theme.colors.textPrimary};
  outline: none;
  caret-color: ${({ theme }) => theme.colors.secondary};
  padding: 0;
  cursor: pointer;

  ${({ isMultiline, theme }) =>
    isMultiline &&
    `
                height: calc(100% - 24px);
                margin-top: ${theme.spacing.spacing120};
            `}
  /* Chrome, Safari, Edge, Opera */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */

  &[type='number'] {
    -moz-appearance: textfield;
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px transparent inset !important;
    -webkit-text-fill-color: ${({ theme }) => theme.colors.textPrimary} !important;
    transition: background-color 5000s ease-in-out 0s;
  }

  &::placeholder {
    letter-spacing: 0.25px;
    text-transform: capitalize;
    opacity: 0;
    ${({ inFocus, theme }) => inFocus && `color: ${theme.colors.textDisabled};opacity: 1;`}
  }
`;

export const TextAreaElement = InputElement.withComponent('textarea');

export const Bar = styled.span<{ isError?: boolean; inFocus: boolean; isValid: boolean }>`
  position: absolute;
  bottom: -3px;
  display: block;
  width: 100%;

  &:after {
    content: '';
    display: block;
    height: 2px;
    width: 100%;
    bottom: 1px;
    position: absolute;
    background: ${({ theme }) => theme.colors.secondary};
    transition: 0.3s ease transform;
    transform: scaleX(0);
    transform-origin: top center;

    ${({ inFocus, isError, isValid }) => {
    if (isError) {
      return `
                    width: calc(100% - 32px); 
                    left: 16px; 
                    transform: scaleX(1); 
                    background: #b60000;
                    border-radius: ${({ theme }) => theme.borders.radius10};
                `;
    }

    if (inFocus) {
      return `transform: scaleX(1);`;
    }

    if (isValid) {
      return ` 
                    transform: scaleX(0); 
                    background-color: transparent;
                `;
    }
  }}
  }
`;

export const HelperText = styled.p<{ isError?: boolean }>`
  margin: 0;
  ${({ theme }) => theme.typography.bodySmall};
  color: ${({ theme }) => theme.colors.textSecondary};
  letter-spacing: 0.5px;
  margin: ${({ theme }) => `${theme.spacing.spacing20} ${theme.spacing.spacing80}`};
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  ${({ isError, theme }) =>
    isError &&
    `
        ${theme.typography.bodySmall};
        letter-spacing: 0.5px;
        margin-left:'auto';
        color: ${theme.colors.negativePrimary};
    `}
`;

export const Sibling = styled.div`
  flex-shrink: 0;
`;

export const Counter = styled.p`
  text-align: right;
  margin: ${({ theme }) => `${theme.spacing.spacing40} 0 0`};
  ${({ theme }) => theme.typography.bodySmall};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export const HelperTextContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;
